import '../styles/style.scss';

import React, { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ModalContextProvider } from 'components/context/modal';
import { DialogOverrideProvider } from 'components/dialogs';
import { DataContextProvider } from 'data/context/data';
import { CartContextProvider } from 'ecommerce/context/cart';
import { UserContextProvider } from 'ecommerce/context/user';
import { EditorContextProvider } from 'editor/context/editorContext';

import AuthCookie from 'ecommerce/lib/authCookie';
import DebugBanner from 'ecommerce/components/debugBanner';
import AppVersion from 'data/components/appVersion';
import getInitialAppData from 'data/lib/getInitialAppData';

import SETTINGS, { updateSettings } from '../settings';
import PRODUCT_EDITOR_CONFIG, { PRODUCT_EDITOR_ALTERNATIVES } from '../specifications/editors';
import PRODUCT_SPECIFICATION from '../specifications/products';
import PRODUCT_CATEGORIES from '../specifications/categories';

import { Provider as RollbarProvider } from '@rollbar/react';
import { createClientConfig, createRollbarInstance } from 'analytics/rollbar';
import RollbarErrorBoundary from 'analytics/RollbarErrorBoundary';

import tracker from '../lib/tracker';
import ErrorPage from './500';
import MaintenancePage from './_maintenance';

const Scripts = dynamic(() => import('../components/document/scripts'), {
  loading: () => null,
  ssr: false,
});

const rollbarInstance = createRollbarInstance(
  createClientConfig({
    accessToken: SETTINGS.SCRIPTS.ROLLBAR_CLIENT_ACCESS_TOKEN,
    environment: SETTINGS.SCRIPTS.ROLLBAR_ENV,
    codeVersion: SETTINGS.SCRIPTS.COMMIT_HASH,
    hostSafeList: ['www.mycanvas.dev', 'mycanvas.dev', 'mycanvas.com', 'www.mycanvas.com', 'localhost'],
  }),
);

const DIALOG_OVERRIDES = {
  'content-wizard:book': {
    title: 'Add to your MyCanvas story',
  },
  'share-creation': {
    description:
      'Your MyCanvas preview will be available to anyone with the link below. They can view and purchase a copy of your project.',
  },
  'editor:final-check': {},
};

const MyCanvasApp = ({ Component, pageProps, errorCode, errorBody, ...appProps }) => {
  const [queryClient] = useState(new QueryClient());
  const router = useRouter();

  const [isRouteChanging, setIsRouteChanging] = useState(false);
  const debug = appProps.debug || SETTINGS.DEBUG;

  const is404 = !!pageProps?.is404 || pageProps?.statusCode === 404;
  const onPageView = useCallback(
    asPath => tracker.pageView(asPath, is404 ? { is_404: true } : {}),
    [is404],
  );

  useEffect(() => {
    updateSettings({ DEBUG: appProps.debug });
  }, []);

  useEffect(() => {
    const handleStart = path => {
      if (router.route !== path) {
        setIsRouteChanging(true);
      }
    };
    const handleStop = () => {
      setIsRouteChanging(false);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router]);

  if (errorCode) {
    if (errorCode === 503) {
      return <MaintenancePage />;
    }
    return <ErrorPage code={errorCode} body={errorBody} />;
  }

  return (
    <RollbarProvider instance={rollbarInstance}>
      <QueryClientProvider client={queryClient}>
        {!debug && <Scripts onAppInit={tracker.onAppInit} onPageView={onPageView} />}
        {/* Omnisend launcher deferred past the load event (queue setup stays
            inline in bodyScripts.js, events buffer until this loads). Keeps
            its cost and lab-only getSettings errors out of the Lighthouse
            window — see beads parabo-next-gto.3 / 4ci.1. */}
        {!debug && (
          <Script
            id="omnisend-launcher"
            src="https://omnisnippet1.com/inshop/launcher-v2.js"
            strategy="lazyOnload"
          />
        )}

        <ModalContextProvider isRouteChanging={isRouteChanging} progressBarColor="#27326C">
          <DialogOverrideProvider overrides={DIALOG_OVERRIDES}>
            <AppVersion appName="MyCanvas" buildId={appProps.buildId} supportedUrls={['/editor']} />

            <DataContextProvider
            productsSpecification={PRODUCT_SPECIFICATION}
            categoriesSpecification={PRODUCT_CATEGORIES}
            realCountryCode={appProps.realCountryCode}
            countryCode={appProps.countryCode}
            skuProducts={appProps.skuProducts}
            productsData={appProps.productsData}
            categoriesData={appProps.categoriesData}
            countries={appProps.countries}
            banner={appProps.banner}
          >
            <UserContextProvider
              storageKey="mycanvas_user_next"
              accessToken={appProps.accessToken}
              onUserSet={tracker.userSet}
              onUserClear={tracker.userClear}
              ancestryCode={router.query.code}
              ancestryState={router.query.state}
              onCustomEvent={tracker.onCustomEvent}
            >
              <CartContextProvider
                storageKey="mycanvas_cart_next"
                promoCode={appProps.query.coupon}
                onCartItemAdd={tracker.cartItemAdd}
                onCartItemUpgrade={tracker.editorUpgradeProduct}
                onCustomEvent={tracker.onCustomEvent}
                onPromoCodeChange={tracker.promoCodeSet}
              >
                <EditorContextProvider
                  platform="mycanvas"
                  storageKey="mycanvas_editor_next"
                  defaultEditor="new"
                  expiration={5}
                  configuration={PRODUCT_EDITOR_CONFIG}
                  alternatives={PRODUCT_EDITOR_ALTERNATIVES}
                  whiteLabeledDomains={SETTINGS.EDITOR_WHITE_LABELED_DOMAINS}
                  onChoosePhotos={tracker.editorChoosePhotos}
                  onUploadPhotos={tracker.editorUploadPhotos}
                  onPreviewPrints={tracker.editorPreviewPrints}
                  onCustomEvent={tracker.onCustomEvent}
                >
                  <RollbarErrorBoundary>
                    <Component {...pageProps} />
                  </RollbarErrorBoundary>

                  {debug && <DebugBanner />}
                </EditorContextProvider>
              </CartContextProvider>
            </UserContextProvider>
            </DataContextProvider>
          </DialogOverrideProvider>
        </ModalContextProvider>
      </QueryClientProvider>
    </RollbarProvider>
  );
};

MyCanvasApp.getInitialProps = async ({ Component, ctx }) => {
  const responseError = (code, body) => {
    if (ctx.res) {
      ctx.res.statusCode = code;
    }
    return {
      errorCode: code,
      errorBody: body,
    };
  };

  try {
    //
    // 1, Get accessToken
    //
    const { value: accessToken, debug: accessTokenDebug } = AuthCookie.get(ctx);

    //
    // 2. Get all necessary data, everything is based on realCountryCode
    //
    const {
      countryCode,
      realCountryCode,
      countries,
      skuProducts,
      productsData,
      categoriesData,
      banner,
    } = await getInitialAppData(ctx, {
      defaultCountryCode: SETTINGS.DEFAULT_COUNTRY_CODE,
      productIds: Object.keys(PRODUCT_SPECIFICATION),
      categoryIds: PRODUCT_CATEGORIES.map(({ id }) => id),
    });

    //
    // 3. Prepare pageProps
    //
    let pageProps = {};
    if (Component.getInitialProps) {
      //
      // Each page receives except ctx also accessToken, countryCode and skuProducts
      //
      pageProps = await Component.getInitialProps({
        ...ctx,
        accessToken,
        countryCode,
        skuProducts,
      });
    }

    //
    // 5. Debug mode
    //
    // TODO - use cookie? routing with debug query param?
    //
    const debug = accessTokenDebug || ctx.query?.debug === 'true';

    return {
      pageProps,
      accessToken,
      countryCode,
      realCountryCode,
      countries,
      skuProducts,
      query: ctx.query,
      productsData,
      categoriesData,
      banner,
      debug,
      buildId: SETTINGS.BUILD_ID,
    };
  } catch (e) {
    return responseError(e.code ?? 500, e.message);
  }
};

export default MyCanvasApp;
